import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { Globe, Menu, X, ChevronDown } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { Button } from '../components/ui/button';

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_9430b0a6-ed13-46f2-84dd-ee0526780bcd/artifacts/xtwfi8dk_logo.png';

export default function Navbar() {
  const { t, language, setLanguage, LANGUAGES } = useLanguage();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => { setMobileOpen(false); }, [location]);

  const navLinks = [
    { to: '/', label: t('nav.home') },
    { to: '/about', label: t('nav.about') },
    { to: '/products', label: t('nav.products') },
    { to: '/production', label: t('nav.production') },
    { to: '/quality', label: t('nav.quality') },
    { to: '/blog', label: 'Blog' },
    { to: '/contact', label: t('nav.contact') },
  ];

  const currentLang = LANGUAGES.find(l => l.code === language);

  return (
    <nav
      data-testid="main-navbar"
      className={`fixed w-full z-50 transition-all duration-500 ${
        scrolled
          ? 'backdrop-blur-xl bg-[#05060A]/85 border-b border-white/10 shadow-[0_0_40px_rgba(59,130,246,0.05)]'
          : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" data-testid="logo-link" className="flex-shrink-0">
            <img src={LOGO_URL} alt="Astravolt" className="h-10 md:h-12 w-auto" />
          </Link>

          {/* Desktop Nav */}
          <div className="hidden lg:flex items-center gap-1">
            {navLinks.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                data-testid={`nav-link-${link.to.replace('/', '') || 'home'}`}
                className={`px-4 py-2 text-sm font-medium tracking-wide uppercase transition-colors duration-300 ${
                  location.pathname === link.to
                    ? 'text-blue-400'
                    : 'text-slate-300 hover:text-white'
                }`}
              >
                {link.label}
              </Link>
            ))}
          </div>

          {/* Language Switcher + Mobile Toggle */}
          <div className="flex items-center gap-3">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  data-testid="language-switcher"
                  className="flex items-center gap-2 text-slate-300 hover:text-white hover:bg-white/10 px-3 py-2"
                >
                  <Globe className="w-4 h-4" />
                  <span className="text-sm font-medium">{currentLang?.native}</span>
                  <ChevronDown className="w-3 h-3" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                align="end"
                className="bg-[#0B0E14] border border-white/10 min-w-[140px]"
              >
                {LANGUAGES.map((lang) => (
                  <DropdownMenuItem
                    key={lang.code}
                    data-testid={`lang-option-${lang.code}`}
                    onClick={() => setLanguage(lang.code)}
                    className={`cursor-pointer text-sm ${
                      language === lang.code ? 'text-blue-400' : 'text-slate-300'
                    } hover:text-white hover:bg-white/10`}
                  >
                    <span className="font-bold mr-2">{lang.native}</span>
                    <span className="text-slate-500">{lang.name}</span>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            <button
              data-testid="mobile-menu-toggle"
              onClick={() => setMobileOpen(!mobileOpen)}
              className="lg:hidden text-slate-300 hover:text-white p-2"
            >
              {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileOpen && (
        <div
          data-testid="mobile-menu"
          className="lg:hidden backdrop-blur-xl bg-[#05060A]/95 border-t border-white/10"
        >
          <div className="px-6 py-6 space-y-1">
            {navLinks.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                data-testid={`mobile-nav-${link.to.replace('/', '') || 'home'}`}
                className={`block px-4 py-3 text-base font-medium tracking-wide uppercase transition-colors ${
                  location.pathname === link.to
                    ? 'text-blue-400 bg-white/5'
                    : 'text-slate-300 hover:text-white hover:bg-white/5'
                }`}
              >
                {link.label}
              </Link>
            ))}
          </div>
        </div>
      )}
    </nav>
  );
}
